import { ActiveRole } from './activeRole';
import { Claim } from './claim';
import { EntitlementLink } from './entitlementLink';
import { HistoryItem } from './historyItem';
import { ManagedUser } from './managedUser';
import { Role } from './role';
import { Substitute } from './substitute';
import { UserSettings } from './userSettings';
import { z } from 'zod';

export const User_Storage = z.object({
    id: z.string(),

    // From AD
    mail: z.string().nullish(),
    displayName: z.string().nullish(),
    givenName: z.string().nullish(),
    surname: z.string().nullish(),
    jobTitle: z.string().nullish(),
    preferredLanguage: z.string().nullish(),
    managerId: z.string().nullish(),
    roles: z.array(Role).nullish(),
    claims: z.array(Claim).nullish(),
    employeeId: z.string().nullish(),
    userType: z.string().nullish(),
    streetAddress: z.string().nullish(),
    postalCode: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    country: z.string().nullish(),
    accountEnabled: z.boolean().nullish(),

    // Mereko
    settings: UserSettings.nullish(),
    outOfOfficeMessage: z.string().nullish(),

    /** userIds of users that can make approval for this user */
    additionalApproverIds: z.array(z.string()).nullish(),
    /** subsitutes inherit this user's approval rights for the given time frame */
    substitutes: z.array(Substitute).nullish(),
    history: z.array(HistoryItem).nullish(),
});
export type User_Storage = z.infer<typeof User_Storage>;

export const User = User_Storage.extend({
    managedUsers: z.array(ManagedUser).nullish(),
    activeRoles: z.array(ActiveRole).nullish(),
    impersonatedBy: z.string().nullish(),
    /**
     * Automated agent (e.g. 'mcp') acting on behalf of this user in the current request, if any.
     * Runtime-only: recorded on history entries the agent creates, never stored on the user.
     */
    agent: z.string().nullish(),
});

export type User = z.infer<typeof User>;

export const UiUser = z.object({
    id: z.string(),
    name: z.string(),
    roles: z.array(Role),
    mail: z.string().nullish(),
    managerId: z.string().nullish(),
    additionalApproverIds: z.array(z.string()).nullish(),
    substitutes: z.array(Substitute).nullish(),
    accountEnabled: z.boolean().nullish(),
});
export type UiUser = z.infer<typeof UiUser>;

export const EntitledUser = User.extend({
    path: z.tuple([EntitlementLink], EntitlementLink),
});
export type EntitledUser = z.infer<typeof EntitledUser>;

export const EntitledUiUser = UiUser.extend({
    path: z.tuple([EntitlementLink], EntitlementLink),
});
export type EntitledUiUser = z.infer<typeof EntitledUiUser>;

export const UserWithManagers = User.extend({
    managers: EntitledUser.array(),
});
export type UserWithManagers = z.infer<typeof UserWithManagers>;

export function getUsername(user: User) {
    if (user.displayName) return user.displayName;
    if (user.givenName || user.surname)
        return [user.givenName, user.surname].filter((x) => x).join(' ');
    if (user.mail) return user.mail;
    return user.id;
}

export function getUiUser(user: User): UiUser {
    return {
        id: user.id,
        mail: user.mail,
        name: getUsername(user),
        roles: user.roles ?? [],
        managerId: user.managerId,
        additionalApproverIds: user.additionalApproverIds,
        substitutes: user.substitutes,
        accountEnabled: user.accountEnabled,
    };
}

export function getEntitledUiUser(user: EntitledUser): EntitledUiUser {
    return {
        ...getUiUser(user),
        path: user.path,
    };
}
